import React, { useState } from 'react';
import { FileImage, Download, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const JPGtoPDF = () => {
    const [images, setImages] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageSize, setPageSize] = useState('a4');
    const [orientation, setOrientation] = useState('portrait');
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFilesSelected = (selectedFiles) => {
        const newImages = selectedFiles.map((file, index) => ({
            id: Date.now() + index,
            file: file,
            name: file.name,
            size: file.size,
            preview: URL.createObjectURL(file)
        }));
        setImages(prev => [...prev, ...newImages]);
        toast.success(`${selectedFiles.length} image(s) added`);
    };

    const removeImage = (id) => {
        setImages(prev => {
            const image = prev.find(img => img.id === id);
            if (image) {
                URL.revokeObjectURL(image.preview);
            }
            return prev.filter(img => img.id !== id);
        });
        toast.info('Image removed');
    };

    const moveImage = (index, direction) => {
        const newImages = [...images];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= images.length) return;

        [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
        setImages(newImages);
    };

    const convertToPDF = async () => {
        if (images.length === 0) {
            toast.warning('Please add at least one image');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const pdfDoc = await PDFDocument.create();
            setProgress(10);

            // Page size dimensions (in points, 72 points = 1 inch)
            const pageSizes = {
                a4: orientation === 'portrait' ? [595, 842] : [842, 595],
                letter: orientation === 'portrait' ? [612, 792] : [792, 612],
                legal: orientation === 'portrait' ? [612, 1008] : [1008, 612]
            };

            const [pageWidth, pageHeight] = pageSizes[pageSize];

            for (let i = 0; i < images.length; i++) {
                const image = images[i];
                setProgress(10 + ((i + 1) / images.length) * 80);

                // Read image file
                const imageBytes = await image.file.arrayBuffer();

                // Embed image (supports JPG and PNG)
                let embeddedImage;
                if (image.file.type === 'image/jpeg' || image.file.type === 'image/jpg') {
                    embeddedImage = await pdfDoc.embedJpg(imageBytes);
                } else if (image.file.type === 'image/png') {
                    embeddedImage = await pdfDoc.embedPng(imageBytes);
                } else {
                    toast.error(`Unsupported image format: ${image.name}`);
                    continue;
                }

                // Create a new page
                const page = pdfDoc.addPage([pageWidth, pageHeight]);

                // Calculate image dimensions to fit page while maintaining aspect ratio
                const imageAspectRatio = embeddedImage.width / embeddedImage.height;
                const pageAspectRatio = pageWidth / pageHeight;

                let drawWidth, drawHeight;
                if (imageAspectRatio > pageAspectRatio) {
                    // Image is wider than page
                    drawWidth = pageWidth - 40; // 20pt margin on each side
                    drawHeight = drawWidth / imageAspectRatio;
                } else {
                    // Image is taller than page
                    drawHeight = pageHeight - 40;
                    drawWidth = drawHeight * imageAspectRatio;
                }

                // Center the image on the page
                const x = (pageWidth - drawWidth) / 2;
                const y = (pageHeight - drawHeight) / 2;

                page.drawImage(embeddedImage, {
                    x,
                    y,
                    width: drawWidth,
                    height: drawHeight,
                });
            }

            setProgress(95);

            // Save PDF
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            // Generate filename
            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `images-to-pdf-${timestamp}.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF created! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert images: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownload = () => {
        if (previewBlob && previewFilename) {
            saveAs(previewBlob, previewFilename);
            toast.success('Download started!');
            setPreviewBlob(null);
            // Clean up images
            images.forEach(img => URL.revokeObjectURL(img.preview));
            setImages([]);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={images.length}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileImage size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    JPG to PDF
                </h1>
                <p className="tool-description">
                    Convert JPG, PNG, and other image formats to PDF. Upload multiple images and combine them into a single PDF document.
                </p>
            </div>

            {/* File Upload */}
            <FileUploadZone
                onFilesSelected={handleFilesSelected}
                acceptedTypes={['image/jpeg', 'image/jpg', 'image/png']}
                multiple={true}
                title="Drop images here"
                description="or click to browse (JPG, PNG supported)"
            />

            {/* Settings */}
            {images.length > 0 && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <Card>
                        <h3 style={{
                            fontSize: 'var(--text-xl)',
                            fontWeight: 'var(--font-bold)',
                            marginBottom: 'var(--spacing-4)'
                        }}>
                            PDF Settings
                        </h3>

                        <div className="grid-2">
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Page Size
                                </label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(e.target.value)}
                                    className="input"
                                >
                                    <option value="a4">A4 (210 × 297 mm)</option>
                                    <option value="letter">Letter (8.5 × 11 in)</option>
                                    <option value="legal">Legal (8.5 × 14 in)</option>
                                </select>
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Orientation
                                </label>
                                <select
                                    value={orientation}
                                    onChange={(e) => setOrientation(e.target.value)}
                                    className="input"
                                >
                                    <option value="portrait">Portrait</option>
                                    <option value="landscape">Landscape</option>
                                </select>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Images List */}
            {images.length > 0 && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <Card>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 'var(--spacing-4)'
                        }}>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)'
                            }}>
                                Images ({images.length})
                            </h3>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    images.forEach(img => URL.revokeObjectURL(img.preview));
                                    setImages([]);
                                }}
                            >
                                Clear All
                            </Button>
                        </div>

                        <Alert variant="info" style={{ marginBottom: 'var(--spacing-4)' }}>
                            Images will appear in the PDF in the order shown below. Use arrows to reorder.
                        </Alert>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                            {images.map((image, index) => (
                                <Card
                                    key={image.id}
                                    variant="outlined"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--spacing-3)',
                                        padding: 'var(--spacing-3)'
                                    }}
                                >
                                    {/* Preview */}
                                    <img
                                        src={image.preview}
                                        alt={image.name}
                                        style={{
                                            width: '60px',
                                            height: '60px',
                                            objectFit: 'cover',
                                            borderRadius: 'var(--radius-md)',
                                            flexShrink: 0
                                        }}
                                    />

                                    {/* Info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            fontWeight: 'var(--font-medium)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {index + 1}. {image.name}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            {formatFileSize(image.size)}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexShrink: 0 }}>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            icon
                                            onClick={() => moveImage(index, 'up')}
                                            disabled={index === 0}
                                            title="Move up"
                                        >
                                            <ArrowUp size={16} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            icon
                                            onClick={() => moveImage(index, 'down')}
                                            disabled={index === images.length - 1}
                                            title="Move down"
                                        >
                                            <ArrowDown size={16} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            icon
                                            onClick={() => removeImage(image.id)}
                                        >
                                            <Trash2 size={16} color="var(--color-error)" />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>

                        {/* Progress Bar */}
                        {isProcessing && (
                            <div style={{ marginTop: 'var(--spacing-6)' }}>
                                <ProgressBar value={progress} striped showLabel />
                            </div>
                        )}

                        {/* Convert Button */}
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={convertToPDF}
                                disabled={isProcessing}
                                loading={isProcessing}
                                fullWidth
                            >
                                <Download size={20} />
                                Convert to PDF
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={handleDownload}
                />
            )}
        </div>
    );
};

export default JPGtoPDF;
